interface TourPageOptions {
  tourSummaryNav: string;
  tourItinerary: string;
  tourAccommodation: string;
  tabbedContent: string;
  tabNextButton: string;
  smoothScroll: string;
  navDropdown: string;
}

const OPTIONS: TourPageOptions = {
  tourSummaryNav: '.tour-summary__nav',
  tourItinerary: '#tour-itinerary',
  tourAccommodation: '#tour-accommodation',
  tabbedContent: '.tabbed-content',
  tabNextButton: '.js-tab-next',
  smoothScroll: 'a.js-smooth-scroll[href*="#"]',
  navDropdown: '.tour-summary__nav-dropdown'
} as const;

const CLASSES = {
  STICKY: 'sticky',
  ACTIVE: 'active',
  D_NONE: 'd-none',
  D_BLOCK: 'd-block',
  IS_OPEN: 'is-open',
  COLLAPSED: 'tabbed-content__item-content--collapsed'
} as const;

const TABBED_ITEM = '.tabbed-content__item';

const ATTRIBUTES = {
  HREF: 'href',
  VALUE: 'value'
} as const;

function isVisibleOnPage(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
}

function getTopFixedOffset(
  tourSummaryNav: HTMLElement,
  opts?: { includeStickyNav?: boolean }
): number {
  let offset = 0;

  const headerCandidates = [
    document.querySelector<HTMLElement>('header'),
    document.querySelector<HTMLElement>('.site-header'),
    document.querySelector<HTMLElement>('.header')
  ].filter((x): x is HTMLElement => !!x);

  const uniqueHeaders = Array.from(new Set(headerCandidates));

  uniqueHeaders.forEach(el => {
    if (!isVisibleOnPage(el)) return;
    const style = window.getComputedStyle(el);
    if (style.position !== 'fixed') return;
    const top = parseFloat(style.top) || 0;
    if (Math.abs(top) > 0.5) return;

    offset += el.getBoundingClientRect().height;
  });

  const includeStickyNav = opts?.includeStickyNav ?? tourSummaryNav.classList.contains(CLASSES.STICKY);
  if (includeStickyNav && isVisibleOnPage(tourSummaryNav)) {
    const navStyle = window.getComputedStyle(tourSummaryNav);
    const navTop = parseFloat(navStyle.top) || 0;
    offset += tourSummaryNav.getBoundingClientRect().height + navTop;
  }

  return Math.ceil(offset);
}

function getScrollDurationMs(distance: number): number {
  let durationMs: number;
  if (distance <= 400) {
    durationMs = 280 + distance * 0.25;
  } else if (distance <= 1200) {
    durationMs = 400 + (distance - 400) * 0.7;
  } else {
    durationMs = 960 + (distance - 1200) * 0.8;
  }

  return Math.max(280, Math.min(2200, Math.round(durationMs)));
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

declare global {
  interface Window {
    GTM: {
      holidayAnchor: (tourName: string, anchorText: string) => void;
    };
    showMore: (section: string) => void;
    showHideList: (section: string) => void;
    showMoreDescription: (section: string, index: number) => void;
    toggleExcursionDescription: (index: number) => void;
  }
}

export function init(): void {
  const tourSummaryElement = document.getElementById('tour-summary');
  if (tourSummaryElement) {
    stickyNav();
  }

  if (document.getElementById('tour-itinerary') || document.getElementById('tour-accommodation')) {
    tabControl();
    tabNext();
    mobileToggles();

    let resizeTimer: number;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        tabControl();
      }, 250);
    });
  }

  if (document.querySelector('.excursion-description')) {
    updateExcursionReadMoreVisibility();

    let excursionResizeTimer: number;
    window.addEventListener('resize', () => {
      clearTimeout(excursionResizeTimer);
      excursionResizeTimer = window.setTimeout(() => {
        updateExcursionReadMoreVisibility();
      }, 250);
    });
  }

  initTourPageFunctions();
}

function initTourPageFunctions(): void {
  window.showMore = showMore;
  window.showHideList = showHideList;
  window.showMoreDescription = showMoreDescription;
  window.toggleExcursionDescription = toggleExcursionDescription;
}

function showMore(section: string): void {
  const showLessElements = document.querySelectorAll<HTMLElement>(`.show-less-${section}`);
  const showMoreElements = document.querySelectorAll<HTMLElement>(`.show-more-${section}`);
  const hiddenElements = document.querySelectorAll<HTMLElement>(`.hidden-${section}`);

  if (showLessElements.length > 0 && showLessElements[0].classList.contains(CLASSES.D_NONE)) {
    showLessElements.forEach(el => el.classList.remove(CLASSES.D_NONE));
    showMoreElements.forEach(el => el.classList.add(CLASSES.D_NONE));
    hiddenElements.forEach(el => el.classList.remove(CLASSES.D_NONE));
    
    if (section === 'excursion') {
      updateExcursionReadMoreVisibility();
    }
  } else {
    showMoreElements.forEach(el => el.classList.remove(CLASSES.D_NONE));
    showLessElements.forEach(el => el.classList.add(CLASSES.D_NONE));
    hiddenElements.forEach(el => el.classList.add(CLASSES.D_NONE));
  }
}

function showHideList(section: string): void {
  const openListElements = document.querySelectorAll<HTMLElement>(`.open-${section}-list`);
  const hideShowElements = document.querySelectorAll<HTMLElement>(`.hide-show-${section}-list`);

  if (openListElements.length > 0 && openListElements[0].classList.contains(CLASSES.D_BLOCK)) {
    openListElements.forEach(el => {
      el.classList.remove(CLASSES.D_BLOCK);
      el.classList.add(CLASSES.D_NONE);
    });
    hideShowElements.forEach(el => el.classList.remove(`${section}__row--open`));
  } else {
    openListElements.forEach(el => {
      el.classList.add(CLASSES.D_BLOCK);
      el.classList.remove(CLASSES.D_NONE);
    });
    hideShowElements.forEach(el => el.classList.add(`${section}__row--open`));
  }
}

function showMoreDescription(section: string, index: number): void {
  const readElement = document.querySelector<HTMLElement>(`.${section}-personalise__read-${index}`);
  const moreElement = document.querySelector<HTMLElement>(`.${section}-description-more-${index}`);
  const lessElement = document.querySelector<HTMLElement>(`.${section}-description-less-${index}`);

  if (!readElement || !moreElement || !lessElement) return;

  if (readElement.classList.contains(`personalise__read--less-${index}`)) {
    readElement.classList.remove(`personalise__read--less-${index}`);
    readElement.classList.remove('personalise__read--less');
    moreElement.classList.add(CLASSES.D_NONE);
    moreElement.classList.remove(CLASSES.D_BLOCK);
    lessElement.classList.add(CLASSES.D_BLOCK);
    lessElement.classList.remove(CLASSES.D_NONE);
  } else {
    readElement.classList.add(`personalise__read--less-${index}`);
    readElement.classList.add('personalise__read--less');
    moreElement.classList.remove(CLASSES.D_NONE);
    moreElement.classList.add(CLASSES.D_BLOCK);
    lessElement.classList.remove(CLASSES.D_BLOCK);
    lessElement.classList.add(CLASSES.D_NONE);
  }
}

function updateExcursionReadMoreVisibility(): void {
  const descriptions = document.querySelectorAll<HTMLElement>(`.excursion-description:not(.${CLASSES.IS_OPEN})`);

  descriptions.forEach(description => {
    const index = description.id.replace('excursion-description-', '');
    const wrapper = document.querySelector<HTMLElement>(`.excursion-personalise__read-${index}`);
    if (!wrapper) return;

    const isClamped = description.scrollHeight > description.clientHeight;
    wrapper.classList.toggle(CLASSES.D_NONE, !isClamped);
  });
}

function toggleExcursionDescription(index: number): void {
  const wrapper = document.querySelector<HTMLElement>(`.excursion-personalise__read-${index}`);
  const toggle = wrapper?.querySelector<HTMLElement>('.personalise__read-toggle');
  const content = document.getElementById(`excursion-description-${index}`);

  if (!toggle || !content) return;

  const isOpen = !content.classList.contains(CLASSES.IS_OPEN);

  content.classList.toggle(CLASSES.IS_OPEN, isOpen);
  toggle.classList.toggle(CLASSES.IS_OPEN, isOpen);
  toggle.setAttribute('aria-expanded', String(isOpen));
  toggle.textContent = isOpen ? toggle.dataset.lessText ?? '' : toggle.dataset.moreText ?? '';
}

function stickyNav(): void {
  const tourSummaryNav = document.querySelector<HTMLElement>(OPTIONS.tourSummaryNav);
  const tourNameElement = document.querySelector<HTMLElement>('h1:first-of-type');

  if (!tourSummaryNav) return;

  const tourSummaryNavOffset = tourSummaryNav.getBoundingClientRect().top + window.pageYOffset;
  const tourName = tourNameElement?.textContent?.trim() || '';

  const smoothScrollLinks = document.querySelectorAll<HTMLAnchorElement>(OPTIONS.smoothScroll);
  const sections: { id: string; links: HTMLAnchorElement[]; element: HTMLElement | null }[] = [];
  let clickedSection: string | null = null;

  const getSectionElement = (section: { id: string; element: HTMLElement | null }): HTMLElement | null => {
    if (section.element?.isConnected) return section.element;
    section.element = document.querySelector<HTMLElement>(section.id);
    return section.element;
  };

  const setActiveSection = (activeId: string | null): void => {
    sections.forEach(section => {
      const isActive = section.id === activeId;
      section.links.forEach(link => {
        link.classList.toggle(CLASSES.ACTIVE, isActive);
      });
    });
  };

  const getSectionTop = (section: { id: string; element: HTMLElement | null }): number => {
    const element = getSectionElement(section);
    if (!element) return Number.POSITIVE_INFINITY;
    return element.getBoundingClientRect().top + window.pageYOffset;
  };

  const computeTargetTop = (targetElement: HTMLElement, scrollY: number): number => {
    const absoluteTop = targetElement.getBoundingClientRect().top + scrollY;
    const includeStickyNav = Math.max(scrollY, absoluteTop) >= tourSummaryNavOffset;
    const offset = getTopFixedOffset(tourSummaryNav, { includeStickyNav });
    let top = absoluteTop - offset;

    const id = `#${targetElement.id}`;
    if (id === '#tour-summary') {
      top = Math.max(top, tourSummaryNavOffset);
    }

    return Math.max(0, Math.round(top));
  };

  const scrollToElement = (
    targetElement: HTMLElement,
    behavior: ScrollBehavior,
    opts?: { updateHash?: string; enableCorrection?: boolean; onComplete?: () => void }
  ): void => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    const effectiveBehavior: ScrollBehavior = reduceMotion ? 'auto' : behavior;

    const requerySelector = targetElement.id ? `#${CSS.escape(targetElement.id)}` : null;
    let liveTarget = targetElement;
    const getLiveTarget = (): HTMLElement => {
      if (!liveTarget.isConnected && requerySelector) {
        const requeried = document.querySelector<HTMLElement>(requerySelector);
        if (requeried) liveTarget = requeried;
      }
      return liveTarget;
    };

    if (effectiveBehavior === 'smooth') {
      const startY = window.pageYOffset;
      const startTime = performance.now();
      const initialDesired = computeTargetTop(getLiveTarget(), startY);
      const distance = Math.abs(initialDesired - startY);
      const durationMs = getScrollDurationMs(distance);

      const step = (now: number): void => {
        const t = Math.min(1, (now - startTime) / durationMs);
        const eased = easeInOutCubic(t);

        const currentY = window.pageYOffset;
        const desiredY = computeTargetTop(getLiveTarget(), currentY);

        const nextY = currentY + (desiredY - currentY) * eased;
        window.scrollTo({ top: nextY, behavior: 'auto' });

        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          const finalY = computeTargetTop(getLiveTarget(), window.pageYOffset);
          window.scrollTo({ top: finalY, behavior: 'auto' });
          opts?.onComplete?.();
        }
      };

      if (Math.abs(initialDesired - startY) <= 1) {
        window.scrollTo({ top: initialDesired, behavior: 'auto' });
        opts?.onComplete?.();
      } else {
        requestAnimationFrame(step);
      }
    } else {
      const top = computeTargetTop(getLiveTarget(), window.pageYOffset);
      window.scrollTo({ top, behavior: 'auto' });

      const enableCorrection = opts?.enableCorrection ?? true;
      if (enableCorrection) {
        const correct = (): void => {
          const expected = computeTargetTop(getLiveTarget(), window.pageYOffset);
          const delta = expected - window.pageYOffset;
          if (Math.abs(delta) <= 1) return;
          window.scrollTo({ top: expected, behavior: 'auto' });
        };

        requestAnimationFrame(() => requestAnimationFrame(correct));
        window.setTimeout(correct, 80);
        window.setTimeout(correct, 250);
      }

      opts?.onComplete?.();
    }

    if (opts?.updateHash) {
      window.history.pushState(null, '', opts.updateHash);
    }
  };

  smoothScrollLinks.forEach(link => {
    const href = link.getAttribute(ATTRIBUTES.HREF);
    if (!href) return;

    const targetElement = document.querySelector<HTMLElement>(href);
    if (!targetElement) return;

    const existing = sections.find(section => section.id === href);
    if (existing) {
      existing.links.push(link);
    } else {
      sections.push({ id: href, links: [link], element: targetElement });
    }
  });

  const updateActiveNavItem = (): void => {
    if (clickedSection) {
      setActiveSection(clickedSection);

      const navDropdown = document.querySelector<HTMLSelectElement>(OPTIONS.navDropdown);
      if (navDropdown) {
        navDropdown.value = clickedSection;
      }
      return;
    }

    const scrollPosition = window.pageYOffset;
    const navHeight = tourSummaryNav.offsetHeight;
    const offset = navHeight + 100;

    const orderedSections = sections
      .map(section => ({ section, sectionTop: getSectionTop(section) }))
      .sort((a, b) => a.sectionTop - b.sectionTop);

    let currentSection: string | null = null;

    for (let i = orderedSections.length - 1; i >= 0; i--) {
      const { section, sectionTop } = orderedSections[i];
      if (sectionTop === Number.POSITIVE_INFINITY) continue;

      if (scrollPosition >= sectionTop - offset) {
        currentSection = section.id;
        break;
      }
    }

    setActiveSection(currentSection);

    const navDropdown = document.querySelector<HTMLSelectElement>(OPTIONS.navDropdown);
    if (navDropdown && currentSection) {
      navDropdown.value = currentSection;
    }
  };

  const handleScroll = (): void => {
    const currentScrollPosition = window.pageYOffset;

    if (currentScrollPosition >= tourSummaryNavOffset) {
      if (!tourSummaryNav.classList.contains(CLASSES.STICKY)) {
        tourSummaryNav.classList.add(CLASSES.STICKY);
      }
    } else {
      if (tourSummaryNav.classList.contains(CLASSES.STICKY)) {
        tourSummaryNav.classList.remove(CLASSES.STICKY);
      }
    }

    updateActiveNavItem();
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  smoothScrollLinks.forEach(link => {
    link.addEventListener('click', (e: Event) => {
      e.preventDefault();
      const href = link.getAttribute(ATTRIBUTES.HREF);
      if (!href) return;

      clickedSection = href;
      setActiveSection(href);

      const holidayAnchorText = link.textContent || '';
      if (window.GTM?.holidayAnchor) {
        window.GTM.holidayAnchor(tourName, holidayAnchorText);
      }

      const targetElement = document.querySelector<HTMLElement>(href);
      if (!targetElement) {
        clickedSection = null;
        return;
      }

      scrollToElement(targetElement, 'smooth', {
        updateHash: href.startsWith('#') ? href : undefined,
        onComplete: () => {
          clickedSection = null;
          updateActiveNavItem();
        }
      });
    });
  });

  const navDropdown = document.querySelector<HTMLSelectElement>(OPTIONS.navDropdown);
  if (navDropdown) {
    navDropdown.addEventListener('change', () => {
      const selectedValue = navDropdown.value;

      if (!selectedValue || selectedValue === 'Jump to section') return;

      clickedSection = selectedValue;
      setActiveSection(selectedValue);

      const selectedOption = navDropdown.options[navDropdown.selectedIndex];
      const holidayDropdownText = selectedOption.textContent || '';
      if (window.GTM?.holidayAnchor) {
        window.GTM.holidayAnchor(tourName, holidayDropdownText);
      }

      const targetElement = document.querySelector<HTMLElement>(selectedValue);
      if (!targetElement) {
        clickedSection = null;
        return;
      }

      scrollToElement(targetElement, 'smooth', {
        onComplete: () => {
          clickedSection = null;
          updateActiveNavItem();
        }
      });

    });
  }

  updateActiveNavItem();

  const initialHash = window.location.hash;
  if (initialHash && initialHash.startsWith('#')) {
    const initialTarget = document.querySelector<HTMLElement>(initialHash);
    if (initialTarget) {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }

      const urlWithoutHash = `${window.location.pathname}${window.location.search}`;
      window.history.replaceState(null, '', urlWithoutHash);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToElement(initialTarget, 'smooth', { enableCorrection: false });
          window.history.replaceState(null, '', initialHash);
        });
      });
    }
  }
}

// The selected item is shown by the desktop side tabs; on mobile it is also the one the
// disclosure button may expand.
function selectTabbedItem(tabbedContent: HTMLElement, item: HTMLElement): void {
  tabbedContent.querySelectorAll(TABBED_ITEM).forEach(el => el.classList.remove(CLASSES.ACTIVE));
  tabbedContent.querySelectorAll('.tabbed-content__tabs a').forEach(link => link.classList.remove(CLASSES.ACTIVE));

  item.classList.add(CLASSES.ACTIVE);
  tabbedContent
    .querySelector<HTMLAnchorElement>(`.tabbed-content__tabs a[href="#${item.id}"]`)
    ?.classList.add(CLASSES.ACTIVE);
}

// Mobile expanded state: the button's aria-expanded and the panel's collapsed class always
// change together, so the announced state matches what is shown.
function setMobileExpanded(item: HTMLElement, expanded: boolean): void {
  item.querySelector(':scope > .tabbed-content__toggle')?.setAttribute('aria-expanded', String(expanded));
  item.querySelector(':scope > .tabbed-content__item-content')?.classList.toggle(CLASSES.COLLAPSED, !expanded);
}

function tabControl(): void {
  const allTabbedContent = document.querySelectorAll<HTMLElement>(OPTIONS.tabbedContent);

  allTabbedContent.forEach(tabbedContent => {
    const tabs = tabbedContent.querySelector<HTMLElement>('.tabbed-content__tabs');

    if (tabs && isElementVisible(tabs)) {
      const tabLinks = tabs.querySelectorAll<HTMLAnchorElement>('a');

      tabLinks.forEach(link => {
        link.addEventListener('click', (e: Event) => {
          e.preventDefault();
          const target = link.getAttribute(ATTRIBUTES.HREF);

          if (!target) return;

          const targetItem = document.querySelector<HTMLElement>(target);
          if (!targetItem) return;

          selectTabbedItem(tabbedContent, targetItem);
          // Keep the hidden mobile buttons truthful if the viewport later narrows.
          tabbedContent.querySelectorAll<HTMLElement>(TABBED_ITEM).forEach(item => {
            if (item !== targetItem) setMobileExpanded(item, false);
          });

          tabAnchor(targetItem);
        });
      });
    }
  });
}

// Mobile itinerary days and hotels: single-open disclosures. Bound once from init(), not from
// tabControl(), which re-runs on resize and would stack toggles that cancel each other out.
function mobileToggles(): void {
  document.querySelectorAll<HTMLButtonElement>(`${OPTIONS.tabbedContent} .tabbed-content__toggle`).forEach(toggle => {
    toggle.addEventListener('click', () => {
      const item = toggle.closest<HTMLElement>(TABBED_ITEM);
      const tabbedContent = toggle.closest<HTMLElement>(OPTIONS.tabbedContent);
      if (!item || !tabbedContent) return;

      if (toggle.getAttribute('aria-expanded') === 'true') {
        // Stays selected, so the desktop tabs still have a panel to show.
        setMobileExpanded(item, false);
        return;
      }

      const wasActive = item.classList.contains(CLASSES.ACTIVE);
      tabbedContent.querySelectorAll<HTMLElement>(TABBED_ITEM).forEach(other => {
        if (other !== item) setMobileExpanded(other, false);
      });
      selectTabbedItem(tabbedContent, item);
      setMobileExpanded(item, true);

      if (!wasActive) {
        tabAnchor(item);
      }
    });
  });
}

function tabAnchor(targetElement: HTMLElement): void {
  const tourSummaryNav = document.querySelector<HTMLElement>(OPTIONS.tourSummaryNav);

  if (!tourSummaryNav) return;

  const startY = window.pageYOffset;
  const tourSummaryNavOffset = tourSummaryNav.getBoundingClientRect().top + window.pageYOffset;

  const computeTargetTop = (scrollY: number): number => {
    const absoluteTop = targetElement.getBoundingClientRect().top + scrollY;
    const includeStickyNav = Math.max(scrollY, absoluteTop) >= tourSummaryNavOffset;
    const offset = getTopFixedOffset(tourSummaryNav, { includeStickyNav });
    return Math.max(0, Math.round(absoluteTop - offset));
  };

  const startTime = performance.now();
  const initialDesired = computeTargetTop(startY);
  const distance = Math.abs(initialDesired - startY);
  const durationMs = getScrollDurationMs(distance);

  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  if (reduceMotion) {
    window.scrollTo({ top: initialDesired, behavior: 'auto' });
    return;
  }

  const step = (now: number): void => {
    const t = Math.min(1, (now - startTime) / durationMs);
    const eased = easeInOutCubic(t);

    const currentY = window.pageYOffset;
    const desiredY = computeTargetTop(currentY);
    const nextY = currentY + (desiredY - currentY) * eased;
    window.scrollTo({ top: nextY, behavior: 'auto' });

    if (t < 1) {
      requestAnimationFrame(step);
    } else {
      const finalY = computeTargetTop(window.pageYOffset);
      window.scrollTo({ top: finalY, behavior: 'auto' });
    }
  };

  if (Math.abs(initialDesired - startY) <= 1) {
    window.scrollTo({ top: initialDesired, behavior: 'auto' });
  } else {
    requestAnimationFrame(step);
  }
}

function tabNext(): void {
  const tabNextButtons = document.querySelectorAll<HTMLElement>(OPTIONS.tabNextButton);

  tabNextButtons.forEach(button => {
    button.addEventListener('click', (e: Event) => {
      e.preventDefault();

      const tabbedContent = button.closest<HTMLElement>(OPTIONS.tabbedContent);
      if (!tabbedContent) return;

      const activeTab = tabbedContent.querySelector<HTMLAnchorElement>('.tabbed-content__tab-list a.active');
      if (!activeTab) return;

      const activeTabParent = activeTab.parentElement;
      if (!activeTabParent) return;

      const nextTabParent = activeTabParent.nextElementSibling;
      if (!nextTabParent) return;

      const nextTab = nextTabParent.querySelector<HTMLAnchorElement>('a');

      if (nextTab) {
        nextTab.click();
      }
    });
  });
}

function isElementVisible(element: HTMLElement): boolean {
  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
}